import React, { useState, useRef } from 'react';
import { getApiUrl } from '../api/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Upload, FileText, File, CheckCircle, AlertCircle, X, Download, Eye, Clock } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

const DocumentUpload = ({ knowledgeBaseId, onUploadComplete, onClose }) => {
  const [uploadState, setUploadState] = useState('idle'); // idle, uploading, processing, complete, error
  const [uploadProgress, setUploadProgress] = useState(0);
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    tags: ''
  });
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const supportedTypes = {
    'application/pdf': { ext: '.pdf', icon: FileText, color: 'bg-red-100 text-red-600' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { 
      ext: '.docx', icon: FileText, color: 'bg-blue-100 text-blue-600' 
    },
    'text/plain': { ext: '.txt', icon: File, color: 'bg-gray-100 text-gray-600' },
    'text/markdown': { ext: '.md', icon: File, color: 'bg-purple-100 text-purple-600' }
  };

  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const handleFileSelect = (selectedFile) => {
    // Validate file type
    if (!supportedTypes[selectedFile.type] && !selectedFile.name.endsWith('.md')) {
      setError('Unsupported file type. Please upload PDF, DOCX, TXT, or MD files.');
      return;
    }

    // Validate file size
    if (selectedFile.size > maxFileSize) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }

    setFile(selectedFile);
    setError('');
    
    // Auto-fill title from filename if empty
    if (!formData.title) {
      const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, '');
      setFormData(prev => ({ ...prev, title: nameWithoutExt }));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleUpload = async () => {
    if (!file || !formData.title.trim()) {
      setError('Please select a file and provide a title.');
      return;
    }

    setUploadState('uploading');
    setUploadProgress(20);
    setError('');

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('title', formData.title.trim());
      uploadFormData.append('description', formData.description.trim());
      
      // Parse tags
      const tags = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      uploadFormData.append('tags', JSON.stringify(tags));

      setUploadProgress(40);

      const response = await fetch(
        getApiUrl(`/api/knowledge-bases/${knowledgeBaseId}/documents/upload`),
        {
          method: 'POST',
          body: uploadFormData
        }
      );

      setUploadProgress(70);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const result = await response.json();
      setUploadProgress(90);

      // Simulate processing time
      setTimeout(() => {
        setUploadProgress(100);
        setUploadState('processing');
        setResult(result);
        
        // Check processing status periodically
        checkProcessingStatus(result.document_id);
      }, 1000);

    } catch (err) {
      setError(err.message);
      setUploadState('error');
      setUploadProgress(0);
    }
  };

  const checkProcessingStatus = async (documentId) => {
    try {
      // In a real implementation, you'd check the document status
      // For now, simulate processing completion after 3 seconds
      setTimeout(() => {
        setUploadState('complete');
        if (onUploadComplete) {
          onUploadComplete(result);
        }
      }, 3000);
    } catch (err) {
      console.error('Error checking processing status:', err);
      setUploadState('error');
      setError('Processing failed. Please try again.');
    }
  };

  const resetForm = () => {
    setFile(null);
    setFormData({ title: '', description: '', tags: '' });
    setUploadState('idle');
    setUploadProgress(0);
    setError('');
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFileTypeInfo = (file) => {
    const typeInfo = supportedTypes[file.type];
    if (typeInfo) return typeInfo;
    
    // Handle markdown files by extension
    if (file.name.endsWith('.md')) {
      return { ext: '.md', icon: File, color: 'bg-purple-100 text-purple-600' };
    }
    
    return { ext: '', icon: File, color: 'bg-gray-100 text-gray-600' };
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderUploadArea = () => {
    if (file) {
      const typeInfo = getFileTypeInfo(file);
      const IconComponent = typeInfo.icon;
      
      return (
        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
          <div className={`p-2 rounded-lg ${typeInfo.color}`}>
            <IconComponent size={24} />
          </div>
          <div className="flex-1">
            <p className="font-medium">{file.name}</p>
            <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
          </div>
          {uploadState === 'idle' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            >
              <X size={16} />
            </Button>
          )}
        </div>
      );
    }

    return (
      <div
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <p className="text-lg font-medium text-gray-700 mb-2">
          Drop your document here or click to browse
        </p>
        <p className="text-sm text-gray-500 mb-4">
          Supports PDF, DOCX, TXT, and Markdown files (max 10MB)
        </p>
        <Button variant="outline">
          Choose File
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.docx,.txt,.md"
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) handleFileSelect(file);
          }}
        />
      </div>
    );
  };

  const renderProcessingStates = () => {
    if (uploadState === 'uploading') {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Upload className="animate-pulse" size={20} />
            <span className="font-medium">Uploading document...</span>
          </div>
          <Progress value={uploadProgress} />
          <p className="text-sm text-gray-600">
            Uploading and validating your document
          </p>
        </div>
      );
    }

    if (uploadState === 'processing') {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Clock className="animate-spin" size={20} />
            <span className="font-medium">Processing with AI...</span>
          </div>
          <Progress value={uploadProgress} />
          <div className="space-y-2 text-sm text-gray-600">
            <p>✅ Document uploaded successfully</p>
            <p>🔍 Analyzing content with LLM...</p>
            <p>🧠 Generating vector embeddings...</p>
            <p>⚡ Preparing for RAG agents...</p>
          </div>
        </div>
      );
    }

    if (uploadState === 'complete') {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle size={20} />
            <span className="font-medium">Document processed successfully!</span>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-green-800 mb-2">
              <strong>Document:</strong> {result?.document_id}
            </p>
            <p className="text-sm text-green-800 mb-2">
              <strong>Version:</strong> {result?.version}
            </p>
            <p className="text-sm text-green-800">
              Your document has been analyzed by AI and vectorized for intelligent retrieval.
              RAG agents can now use this content for chain-of-thought reasoning.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={resetForm}>
              Upload Another
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      );
    }

    if (uploadState === 'error') {
      return (
        <Alert className="border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            {error}
          </AlertDescription>
        </Alert>
      );
    }

    return null;
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Upload Document to Knowledge Base
        </CardTitle>
        <p className="text-sm text-gray-600">
          Upload documents for AI analysis, vectorization, and intelligent RAG agent reasoning
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && uploadState === 'idle' && (
          <Alert className="border-red-200">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {uploadState === 'idle' && (
          <>
            {renderUploadArea()}
            
            {file && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Document title"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Description</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of the document content"
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Tags</label>
                  <Input
                    value={formData.tags}
                    onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                    placeholder="tag1, tag2, tag3"
                  />
                  <p className="text-xs text-gray-500">
                    Separate multiple tags with commas
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button 
                    onClick={handleUpload}
                    className="flex-1"
                    disabled={!formData.title.trim()}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Upload & Process
                  </Button>
                  <Button variant="outline" onClick={resetForm}>
                    Clear
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {renderProcessingStates()}
      </CardContent>
    </Card>
  );
};

export default DocumentUpload;